import { addPurchase, money } from './tb-purchase-core.js';

export function confirmGift(cart, items, token) {
  return items.every(expected => cart.items.filter(line =>
    String(line.variant_id || line.id) === String(expected.id) &&
    line.properties?._tb_gift_selection === token && !line.selling_plan_allocation
  ).reduce((sum, line) => sum + Number(line.quantity), 0) === expected.quantity);
}

export function giftItems(mug, set, token) {
  if (!mug?.id || !set?.id || !mug.available || !set.available || !set.withStand) throw new Error('SELECTION');
  if (String(mug.id) === String(set.id)) throw new Error('SELECTION');
  return [mug, set].map(item => ({ id: Number(item.id), quantity: 1, properties: { _tb_gift_selection: token } }));
}

function sizedImage(url, width) { const target = new URL(url, window.location.origin); target.searchParams.set('width', width); return target.href; }

class MouseGiftBuilder extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.cfg = JSON.parse(this.querySelector('[data-gift-config]').textContent);
    this.words = JSON.parse(this.querySelector('[data-gift-words]').textContent);
    this.mugKey = this.cfg.mugs[0]?.key;
    this.setKey = this.cfg.sets[0]?.key;
    this.busy = false;
    this.locked = false;
    this.$ = selector => this.querySelector(selector);
    this.addEventListener('change', event => {
      if (event.target.matches('[data-gift-mug]')) this.mugKey = event.target.value;
      if (event.target.matches('[data-gift-set]')) this.setKey = event.target.value;
      this.render(true);
    }, { signal: this.controller.signal });
    this.addEventListener('click', event => {
      const thumbnail = event.target.closest('[data-gift-thumb]');
      if (thumbnail) this.showImage(Number(thumbnail.dataset.giftThumb));
      if (event.target.closest('[data-gift-add]')) this.submit();
    }, { signal: this.controller.signal });
    this.render(true);
  }
  disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  render(gallery = false) {
    this.mug = this.cfg.mugs.find(item => item.key === this.mugKey);
    this.set = this.cfg.sets.find(item => item.key === this.setKey);
    if (!this.mug || !this.set) return;
    this.querySelectorAll('input[type=radio]').forEach(input => {
      input.checked = input.value === (input.hasAttribute('data-gift-mug') ? this.mugKey : this.setKey);
      input.disabled = this.busy;
    });
    const bf=window.TeaballsBFPricing;
    const mugPrice=bf?.active()?bf.price(this.mug.id,this.mug.price):this.mug.price;
    const setPrice=bf?.active()?bf.price(this.set.id,this.set.price):this.set.price;
    const total = mugPrice + setPrice;
    if(bf?.active())bf.setAmounts(this.$('[data-gift-total]'),this.mug.price+this.set.price,total);else this.$('[data-gift-total]').textContent = money(total, this.cfg.currency);
    this.$('[data-gift-summary]').textContent = this.mug.label + ' + ' + this.set.label;
    if(bf?.active())bf.setAmounts(this.$('[data-gift-mug-price]'),this.mug.price,mugPrice);else this.$('[data-gift-mug-price]').textContent = money(mugPrice, this.cfg.currency);
    if(bf?.active())bf.setAmounts(this.$('[data-gift-set-price]'),this.set.price,setPrice);else this.$('[data-gift-set-price]').textContent = money(setPrice, this.cfg.currency);
    this.$('[data-gift-selected-set]').textContent = this.set.label;
    const ready = this.mug.available && this.set.available && this.set.withStand && this.mug.id && this.set.id;
    this.$('[data-gift-add]').disabled = this.busy || this.locked || !ready;
    this.$('[data-gift-add]').setAttribute('aria-busy', String(this.busy));
    this.$('[data-gift-add]').textContent = this.busy ? this.words.adding : ready ? this.words.add : this.words.unavailable;
    this.$('[data-gift-preview]').hidden = !this.mug.preview;
    this.$('[data-gift-sku]').textContent = this.mug.sku + ' + ' + this.set.sku;
    if (gallery) {
      this.gallery = this.cfg.galleries.find(item => item.mug === this.mugKey && item.set === this.setKey)?.images || [];
      this.$('[data-gift-thumbs]').replaceChildren();
      this.gallery.forEach((image, index) => {
        const button = document.createElement('button');
        button.type = 'button'; button.dataset.giftThumb = index;
        button.setAttribute('aria-label', this.words.image.replace('[number]', index + 1));
        const img = document.createElement('img'); img.src = sizedImage(image.url, 144); img.alt = ''; img.width = 72; img.height = 72; img.loading = 'lazy';
        button.append(img); this.$('[data-gift-thumbs]').append(button);
      });
      this.showImage(0);
    }
  }
  showImage(index) {
    const image = this.gallery?.[index]; if (!image) return;
    const hero = this.$('[data-gift-hero]'); if (!hero) return;
    hero.src = sizedImage(image.url, 1100);
    hero.srcset = [400, 700, 1100, 1600].map(width => sizedImage(image.url, width) + ' ' + width + 'w').join(',');
    hero.alt = image.alt;
    this.querySelectorAll('[data-gift-thumb]').forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.giftThumb) === index)));
  }
  async submit() {
    if (this.busy || this.locked || this.$('[data-gift-add]').disabled) return;
    const error = this.$('[data-gift-error]'); error.hidden = true;
    this.$('[data-gift-success]').hidden = true;
    this.busy = true; this.render();
    const root = window.Shopify?.routes?.root || '/';
    let posted = false;
    try {
      for (const item of [this.mug, this.set]) {
        const response = await fetch(root + 'products/' + encodeURIComponent(item.handle) + '.js', { cache: 'no-store', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(this.words.changed);
        const product = await response.json();
        const variant = product.variants.find(variant => String(variant.id) === String(item.id));
        if (!variant?.available || variant.sku !== item.sku || Number(variant.price) !== item.price) throw new Error(this.words.changed);
      }
      const token = crypto.randomUUID();
      const items = giftItems(this.mug, this.set, token);
      posted = true;
      let timer;
      const timeout = new Promise((_, reject) => { timer = setTimeout(() => { const error = new Error(this.words.incomplete); error.uncertain = true; reject(error); }, 20000); });
      let cart;
      try { cart = await Promise.race([addPurchase(items), timeout]); } finally { clearTimeout(timer); }
      if (!confirmGift(cart, items, token)) {
        const failure = new Error(this.words.incomplete); failure.uncertain = true; throw failure;
      }
      this.$('[data-gift-success]').textContent = this.words.success;
      this.$('[data-gift-success]').hidden = false;
    } catch (failure) {
      this.locked = posted || Boolean(failure.uncertain);
      error.textContent = failure.message === 'SELECTION' ? this.words.changed : failure.message || this.words.failed;
      error.hidden = false;
      error.focus();
    } finally { this.busy = false; this.render(); }
  }
}
if (!customElements.get('tb-mouse-gift-builder')) customElements.define('tb-mouse-gift-builder', MouseGiftBuilder);
